import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { crossIcon } from "../../../assets/icons";
import {
  MODAL_SIZE,
  MODAL_POSITION,
  KEYDOWN,
  ESCAPE,
  WINDOW_EVENTS,
} from "../../../constants";
import { selectSelectedModal } from "./index";
import Alert, { selectAlert } from "../Alert";

const { MD, FULL, LG, XL } = MODAL_SIZE;
const { CENTER } = MODAL_POSITION;
const { POPSTATE } = WINDOW_EVENTS;

const Modal = ({
  title,
  subtitle,
  footer,
  children,
  size = MD,
  position = CENTER,
  handleReset = () => {},
  handleClose,
  shouldOpen,
  shouldShowAlertMessage = true,
}) => {
  const selectedModal = useSelector(selectSelectedModal);
  const selectedAlert = useSelector(selectAlert);
  const backdropRef = useRef(null);

  const [shouldShow, setShouldShow] = useState(false);
  const [alertData, setAlertData] = useState(selectedAlert);

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    setShouldShow(selectedModal || shouldOpen);
  }, [selectedModal, shouldOpen]);

  useEffect(() => {
    const close = ({ code }) => code === ESCAPE && handleClose();
    document.addEventListener(KEYDOWN, close);
    return () => document.removeEventListener(KEYDOWN, close);
  }, [handleClose]);

  useEffect(() => {
    window.addEventListener(POPSTATE, handleClose);
    return () => window.removeEventListener(POPSTATE, handleClose);
  }, [shouldShow, handleClose]);

  return (
    <div
      className={classNames(
        "fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] overflow-auto transition-opacity duration-300",
        {
          " px-4 flex": size === MD || LG,
          " items-center": (size === MD || LG) && position === CENTER,
          " inline-block opacity-100": !!shouldShow,
          " w-0 h-0 opacity-0": !shouldShow,
        }
      )}
      onClick={handleClose}
    >
      <div
        ref={backdropRef}
        onClick={(e) => e.stopPropagation()}
        className={classNames(
          "bg-gray-800 border border-gray-700 rounded  mx-auto",
          {
            " my-6 sm:w-[600px] h-fit": size === MD,
            " my-6 sm:w-[800px] overflow-y-auto max-h-[600px] scrollbar":
              size === LG,
            " h-screen flex w-full flex-col": size === FULL,
            "w-3/4 flex-col h-fit": size === XL,
          }
        )}
      >
        {title && (
          <div className="flex items-center p-6 border-b border-gray-700">
            <div>
              <h6
                className={classNames("text-base text-gray-50 font-bold", {
                  "mb-2": !!subtitle,
                })}
              >
                {title}
              </h6>
              <p className="text-sm text-gray-50">{subtitle}</p>
            </div>
            {handleClose && (
              <button
                type="button"
                className="ml-auto rounded-md bg-gray-900"
                onClick={() => {
                  handleReset();
                  handleClose();
                }}
              >
                <img src={crossIcon} alt="cross" className="w-6" />
              </button>
            )}
          </div>
        )}
        {alertData && shouldShowAlertMessage && (
          <div className="my-2 mx-7">
            <Alert
              type={alertData.type}
              message={alertData.message}
              handleOnClose={setAlertData}
            />
          </div>
        )}
        {children}
        {footer}
      </div>
    </div>
  );
};

export default Modal;
